import { useEffect, useState } from 'react'
import { useEnv } from '@praxis/component-runtime-env'
import _ from 'lodash'
import { Input, Grid, DatePicker } from '@design-systems/canvas-ui'
import { AutocompleteInput } from '../../common/AutocompleteInput'
import { FileHandler } from 'mvscommoncomponents'
import {
  buildDropdownOptions,
  getControlError,
  getSubstrateType,
  isDefaultCertName,
  isOthersInArray,
} from '../../common/helper'
import { renderControlLabel, showErrorMessage } from '../Builders'
import {
  FACTORY_REGISTRATION_LICENSE,
  MANUFACTURING_TYPE_ID_PACKAGING,
  RFI_TYPE_LOCATION,
  VALUE_OTHERS,
} from '../../common/commonConstants'
import {
  uploadFiles,
  downloadFile,
  deleteFile,
} from '../../services/lcmService'

const FIELD_OTHER_CERT_NAME = 'other_certificate_name'
const FIELD_CERT_NAME = 'certificate_name'
const FIELD_ISSUING_ORGANISATION = 'issuing_organisation'
const FIELD_MATERIALS_CERTIFIED = 'materials_certified'
const FIELD_OTHER_MATERIALS_CERTIFIED = 'other_materials_certified'
const FIELD_ISSUE_DATE = 'issue_date'
const FIELD_EXPIRY_DATE = 'expiry_date'
const FIELD_ADDITIONAL_REMARKS = 'additional_remarks'

const CertificateDetails = (props) => {
  const env = useEnv()
  const [certInfo, setCertInfo] = useState(props.certInfo)

  //File Upload
  const [isFileActionInProgress, setIsFileActionInProgress] = useState(false)
  const [isFileActionFailed, setIsFileActionFailed] = useState(false)

  //#region UseEffects

  useEffect(() => {
    if (!_.isEqual(certInfo, props.certInfo)) {
      props.updateCertDetails(certInfo, props.certIndex)
    }
  }, [certInfo]) // eslint-disable-line react-hooks/exhaustive-deps

  //#endregion

  const updateCertDetails = (property, newValue) => {
    if (property === FIELD_CERT_NAME) {
      if (!newValue) {
        setCertInfo({
          ...certInfo,
          [property]: newValue,
          [FIELD_CERT_NAME]: '',
        })
      } else if (certInfo[FIELD_CERT_NAME] !== 'Others') {
        setCertInfo({
          ...certInfo,
          [property]: newValue,
          [FIELD_OTHER_CERT_NAME]: '',
        })
      }
    } else {
      setCertInfo({ ...certInfo, [property]: newValue })
    }
  }

  const renderCertDetails = () => {
    let nameError = getControlError(
      `${props.sectionIndexPath}.${FIELD_CERT_NAME}`,
      props.errorCache,
    )
    let otherNameError = getControlError(
      `${props.sectionIndexPath}.${FIELD_OTHER_CERT_NAME}`,
      props.errorCache,
    )
    let issueOrgError = getControlError(
      `${props.sectionIndexPath}.${FIELD_ISSUING_ORGANISATION}`,
      props.errorCache,
    )
    let materialsCertifiedError = getControlError(
      `${props.sectionIndexPath}.${FIELD_MATERIALS_CERTIFIED}`,
      props.errorCache,
    )
    let otherMaterialsCertifiedError = getControlError(
      `${props.sectionIndexPath}.${FIELD_OTHER_MATERIALS_CERTIFIED}`,
      props.errorCache,
    )
    let issueDateError = getControlError(
      `${props.sectionIndexPath}.${FIELD_ISSUE_DATE}`,
      props.errorCache,
    )
    let tillDateError = getControlError(
      `${props.sectionIndexPath}.${FIELD_EXPIRY_DATE}`,
      props.errorCache,
    )
    let remarksError = getControlError(
      `${props.sectionIndexPath}.${FIELD_ADDITIONAL_REMARKS}`,
      props.errorCache,
    )

    return (
      <>
        <Grid.Container className="hc8-mb-3x hc8-mt-3x">
          {/* 1. Name */}
          <Grid.Item size={{ xs: 3 }} className="hc8-pt-none">
            <div style={{ width: '100%' }}>
              {renderControlLabel({ label_name: 'Name', is_mandatory: true })}
              {
                props.rfiType.toLowerCase() === RFI_TYPE_LOCATION &&
                isDefaultCertName(certInfo, props?.manufacturingTypeId) ? (
                  <Input.Label className="hc8-fs-100 hc8-mt-3x hc8-pt-2x">
                    {certInfo.certificate_name}
                  </Input.Label>
                ) : (
                  <>
                    <AutocompleteInput
                      key={`ac-cert-name-${props.certIndex}`}
                      value={certInfo[FIELD_CERT_NAME]}
                      class="font-size-16 height-32p"
                      error={nameError}
                      options={buildDropdownOptions(
                        props.rfiMasterData.common?.certification_names,
                        null,
                        true,
                        false,
                      )}
                      onUpdate={(value) => {
                        updateCertDetails(FIELD_CERT_NAME, value?.value)
                      }}
                    />
                    {certInfo.certificate_name?.toLowerCase() ===
                      VALUE_OTHERS && (
                      <Input.Text
                        key={`tb-other-cert-name-${props.certIndex}`}
                        className="hc8-mt-none hc8-pt-none hc8-mb-3x"
                        value={certInfo[FIELD_OTHER_CERT_NAME]}
                        placeholder={'Mention name of Certificate'}
                        isErrored={!!otherNameError}
                        onChange={(event) => {
                          updateCertDetails(
                            FIELD_OTHER_CERT_NAME,
                            event.target.value,
                          )
                        }}
                      />
                    )}
                    <div className="hc8-pt-2x">
                      {showErrorMessage(nameError)}
                    </div>
                    {otherNameError && (
                      <div className="hc8-pt-none">
                        {showErrorMessage(otherNameError)}
                      </div>
                    )}
                  </>
                )
                //)
              }
            </div>
          </Grid.Item>

          {/* 2. Issuing Organization */}
          <Grid.Item size={{ xs: 3 }} className="hc8-pt-none">
            <div style={{ width: '100%' }}>
              {renderControlLabel({
                label_name: 'Issuing Organization',
                is_mandatory: true,
              })}
              <Input.Text
                key={`tb-issue-org-${props.certIndex}`}
                className="hc8-mt-1x"
                value={certInfo[FIELD_ISSUING_ORGANISATION]}
                isErrored={!!issueOrgError}
                onChange={(event) => {
                  updateCertDetails(
                    FIELD_ISSUING_ORGANISATION,
                    event.target.value,
                  )
                }}
              />
              <div className="hc8-pt-2x">{showErrorMessage(issueOrgError)}</div>
            </div>
          </Grid.Item>

          {/* 3. Issue Date */}
          <Grid.Item
            id="grid-item-issue-date"
            size={{ xs: 3 }}
            className="hc8-pt-none"
          >
            <div style={{ width: '100%' }}>
              {renderControlLabel({
                label_name: 'Issue Date',
                is_mandatory: true,
              })}
              <DatePicker
                key={`dp-issue-date-${props.certIndex}`}
                id={`dp-issue-date-${props.certIndex}`}
                className="hc8-mt-1x"
                value={certInfo[FIELD_ISSUE_DATE]?.replaceAll('-', '/')}
                format="YYYY/MM/DD"
                onChange={(id, value) => {
                  let newDateValue = value?.replaceAll('/', '-') || null
                  updateCertDetails(FIELD_ISSUE_DATE, newDateValue)
                }}
                isErrored={!!issueDateError}
                location="bottom-left"
                disableDates={{
                  future: true,
                }}
                hintText={'Issue Date cannot be in the future'}
              />
              <div className="hc8-pt-2x">
                {showErrorMessage(issueDateError)}
              </div>
            </div>
          </Grid.Item>

          {/* 4. Valid Till Date */}
          <Grid.Item
            id="grid-item-valid-till-date"
            size={{ xs: 3 }}
            className="hc8-pt-none"
          >
            <div style={{ width: '100%' }}>
              {renderControlLabel({
                label_name: 'Valid Till Date',
                is_mandatory: true,
              })}
              <DatePicker
                key={`dp-valid-till-date-${props.certIndex}`}
                id={`dp-valid-till-date-${props.certIndex}`}
                className="hc8-mt-1x"
                value={certInfo[FIELD_EXPIRY_DATE]?.replaceAll('-', '/')}
                onChange={(id, value) => {
                  let newDateValue = value?.replaceAll('/', '-') || null
                  updateCertDetails(FIELD_EXPIRY_DATE, newDateValue)
                }}
                format="YYYY/MM/DD"
                location="bottom-left"
                isErrored={!!tillDateError}
                isDisabled={!certInfo[FIELD_ISSUE_DATE]}
                disableDates={
                  certInfo[FIELD_ISSUE_DATE]
                    ? {
                        before: certInfo[FIELD_ISSUE_DATE].replaceAll('-', '/'),
                      }
                    : undefined
                }
                hintText={'Valid Till Date cannot be before Issue Date'}
              />
              <div className="hc8-pt-2x">{showErrorMessage(tillDateError)}</div>
            </div>
          </Grid.Item>

          {/* 5. Additional Remarks */}
          <Grid.Item size={{ xs: 3 }} className="hc8-pt-none">
            <div style={{ width: '100%' }}>
              {renderControlLabel({ label_name: 'Additional Remarks' })}
              <Input.Text
                key={`tb-add-remarks-${props.certIndex}`}
                data-testid={`tb-add-remarks-${props.certIndex}`}
                className="hc8-mt-1x"
                isErrored={!!remarksError}
                value={certInfo[FIELD_ADDITIONAL_REMARKS]}
                onChange={(event) => {
                  updateCertDetails(
                    FIELD_ADDITIONAL_REMARKS,
                    event.target.value,
                  )
                }}
              />
              <div className="hc8-pt-2x">{showErrorMessage(remarksError)}</div>
            </div>
          </Grid.Item>

          {/* 6. Materials Certified for Packaging */}
          {props.rfiType.toLowerCase() === RFI_TYPE_LOCATION &&
            props.manufacturingTypeId === MANUFACTURING_TYPE_ID_PACKAGING &&
            certInfo?.certificate_name?.toLowerCase() !==
              FACTORY_REGISTRATION_LICENSE && (
              <>
                <Grid.Item size={{ xs: 3 }} className="hc8-pt-none">
                  <div style={{ width: '100%' }}>
                    {renderControlLabel({
                      label_name: 'Materials Certified',
                    })}
                    <AutocompleteInput
                      key={`ac-cert-materials-certified-${props.certIndex}`}
                      value={buildDropdownOptions(
                        certInfo[FIELD_MATERIALS_CERTIFIED]
                          ? certInfo[FIELD_MATERIALS_CERTIFIED]
                          : [],
                        null,
                        false,
                        false,
                      )}
                      class="font-size-16"
                      multiselect={true}
                      error={materialsCertifiedError}
                      options={buildDropdownOptions(
                        getSubstrateType(
                          props.rfiMasterData?.packaging?.primary_materials,
                        ),
                        null,
                        true,
                        false,
                      )}
                      onUpdate={(value) => {
                        let newList = value?.map((eachValue) => {
                          return eachValue.value
                        })
                        updateCertDetails(FIELD_MATERIALS_CERTIFIED, newList)
                      }}
                    />
                    {materialsCertifiedError && (
                      <div className="hc8-pt-2x">
                        {showErrorMessage(materialsCertifiedError)}
                      </div>
                    )}
                  </div>
                  {certInfo[FIELD_MATERIALS_CERTIFIED] &&
                    isOthersInArray(certInfo[FIELD_MATERIALS_CERTIFIED]) && (
                      <div>
                        <Input.Text
                          key={`ac-cert-materials-certified-other-${props.certIndex}`}
                          id={`ac-cert-materials-certified-other-${props.certIndex}`}
                          className="hc8-mt-none hc8-pt-none"
                          value={certInfo[FIELD_OTHER_MATERIALS_CERTIFIED]}
                          isErrored={!!otherMaterialsCertifiedError}
                          placeholder={
                            'Enter value of Other Materials Certified'
                          }
                          onChange={(event) => {
                            updateCertDetails(
                              FIELD_OTHER_MATERIALS_CERTIFIED,
                              event.target.value,
                            )
                          }}
                        />
                        {otherMaterialsCertifiedError && (
                          <div className="hc8-pt-2x">
                            {showErrorMessage(otherMaterialsCertifiedError)}
                          </div>
                        )}
                      </div>
                    )}
                </Grid.Item>
              </>
            )}
        </Grid.Container>
        <Grid.Container className="hc8-mb-3x">
          <Grid.Item size={{ xs: 12 }}>
            <Input.Label className="hc8-fs-150 font-weight-500 hc8-mb-2x">
              Upload Documents/Images/Video
            </Input.Label>
          </Grid.Item>
          <Grid.Item size={{ xs: 6 }}>
            <FileHandler
              fileUploadConstants={env?.mfelcm?.rfiFileUploadConstants}
              isFileActionInProgress={isFileActionInProgress}
              isFileActionFailed={isFileActionFailed}
              fileHandlerData={certInfo?.file_upload}
              uploadFiles={(files) => {
                return uploadCertFiles(files)
              }}
              downloadFiles={(fileName, tossKey) => {
                return downloadCertFile(fileName, tossKey)
              }}
              deleteFiles={(fileName) => deleteCertFile(fileName)}
            />
          </Grid.Item>
          <Grid.Item size={{ xs: 6 }}>{showFileRestrictions()}</Grid.Item>
        </Grid.Container>
      </>
    )
  }

  //#region Uploads

  /**
   * Upload cert files
   * @param {*} files
   * @returns
   */
  const uploadCertFiles = async (files) => {
    try {
      setIsFileActionInProgress(true)
      setIsFileActionFailed(false)
      let response = await uploadFiles(
        props.rfiType,
        props.componentPath,
        false,
        files,
        props.certIndex,
        props.supplierId,
        props.locationId,
        props.manufacturingTypeId,
      )
      setCertInfo({
        ...certInfo,
        file_upload: {
          uploaded_files: {
            ...certInfo.file_upload.uploaded_files,
            ...response.files,
          },
        },
      })
      setIsFileActionInProgress(false)
      setIsFileActionFailed(false)
      return response
    } catch (error) {
      setIsFileActionInProgress(false)
      setIsFileActionFailed(true)
      return error
    }
  }

  /**
   * Download cert file
   * @param {*} fileName
   * @param {*} tossKey
   * @returns
   */
  const downloadCertFile = async (fileName, tossKey) => {
    try {
      setIsFileActionInProgress(true)
      setIsFileActionFailed(false)
      let response = await downloadFile(
        props.rfiType,
        props.componentPath,
        fileName,
        tossKey,
        props.certIndex,
      )
      setIsFileActionInProgress(false)
      setIsFileActionFailed(false)
      return response
    } catch (error) {
      setIsFileActionInProgress(false)
      setIsFileActionFailed(true)
      return error
    }
  }

  /**
   * Delete cert file
   * @param {*} fileName
   * @returns
   */
  const deleteCertFile = async (fileName) => {
    try {
      setIsFileActionInProgress(true)
      setIsFileActionFailed(false)

      //1. Delete file
      let response = await deleteFile(
        props.rfiType,
        props.componentPath,
        fileName,
        props.certIndex,
        props.supplierId,
        props.locationId,
        props.manufacturingTypeId,
      )
      // 2. Post delete success - update state
      let fileList = { ...certInfo.file_upload?.uploaded_files }
      delete fileList[fileName]
      setCertInfo({
        ...certInfo,
        file_upload: {
          uploaded_files: fileList,
        },
      })
      setIsFileActionInProgress(false)
      setIsFileActionFailed(false)
      return response
    } catch (error) {
      setIsFileActionInProgress(false)
      setIsFileActionFailed(true)
      return error
    }
  }

  /**
   * Justification column popover
   * @returns
   */
  const showFileRestrictions = () => {
    let data = `<p>1. Accepted file formats are <strong>${env?.mfelcm?.rfiFileUploadConstants?.acceptedFileFormats}</strong>. <br />
                    2. Max size allowed for each file is <strong>${env?.mfelcm?.rfiFileUploadConstants?.maxFileSize}</strong> and Max total size
                    allowed for all files in one upload is <strong>${env?.mfelcm?.rfiFileUploadConstants?.maxUploadSize}</strong>.</p>`
    return (
      <p
        dangerouslySetInnerHTML={{
          __html: [data],
        }}
      ></p>
    )
  }

  //#endregion

  return <div>{certInfo && renderCertDetails()}</div>
}

export default CertificateDetails
